import convert from 'convert';
import { Forma } from 'forma-embedded-view-sdk/auto';

export type UnitSystem = 'metric' | 'imperial';
export type MeasurementType = 'length' | 'area';

export type Unit = 'm' | 'ft' | 'm2' | 'ft2';

const unitMap: Record<UnitSystem, Record<MeasurementType, Unit>> = {
	metric: { length: 'm', area: 'm2' },
	imperial: { length: 'ft', area: 'ft2' },
};

const abbrevMap: Record<Unit, string> = {
	m: 'm',
	ft: 'ft',
	m2: 'm²',
	ft2: 'ft²',
};

class UnitConversionService {
	private currentUnitSystem: UnitSystem = 'metric';

	constructor() {
		this.updateUnitSystem();
	}

	private async updateUnitSystem(): Promise<void> {
		try {
			const formaUnitSystem = await Forma.getPresentationUnitSystem();

			this.currentUnitSystem =
				formaUnitSystem === 'metric' || formaUnitSystem === 'imperial'
					? formaUnitSystem
					: 'metric';
		} catch {
			console.warn('Could not fetch Forma unit system, using default metric.');
		}
	}

	getUnitSystem(): UnitSystem {
		return this.currentUnitSystem;
	}

	getPreferredUnit(type: MeasurementType): Unit {
		return unitMap[this.currentUnitSystem][type];
	}

	getUnitAbbreviation(type: MeasurementType): string {
		return abbrevMap[this.getPreferredUnit(type)];
	}

	convert(value: number, fromUnit: Unit, toUnit: Unit, precision = 0): number {
		try {
			const converted = convert(value, fromUnit).to(toUnit);
			return parseFloat(converted.toFixed(precision));
		} catch (error) {
			console.error(`Unit conversion error: ${value} ${fromUnit} to ${toUnit}`, error);
			return value;
		}
	}

	formatWithPreferredUnit(
		value: number,
		measurementType: MeasurementType,
		fromUnit: Unit,
		precision = 0
	): string {
		const unit = this.getPreferredUnit(measurementType);
		const converted = this.convert(value, fromUnit, unit);

		return `${converted.toFixed(precision)} ${this.getUnitAbbreviation(measurementType)}`;
	}

	toPreferredUnit(value: number, measurementType: MeasurementType, fromUnit: Unit): number {
		const unit = this.getPreferredUnit(measurementType);
		const converted = this.convert(value, fromUnit, unit);

		return Math.round(converted);
	}
}

export const unitConversionService = new UnitConversionService();
